import SlotCard from './SlotCard'
import {
  generateDates,
  generateDatedSlots,
  isValidMedicationSlotWithProviderId,
  isValidMedicationSlotWithProviderFilter,
  groupFilters,
} from 'app/ProviderSearch/utils'
import { getProviderSlots } from 'context/ProviderBooking/ProviderSlots'
import React, {
  useContext,
  useMemo,
  forwardRef,
  useEffect,
  useState,
} from 'react'
import useAppSelector from 'hooks/useAppSelector'
import { useDispatch } from 'react-redux'
import {
  formatUserQuery,
  countProviderSlots,
  getCurrentZonnedTime,
  getParsedQuery,
  filterDateSlotsByAppointmentMode,
  getSlotWithTimeOfDayAdded,
} from 'utils/common'
import { useRouter } from 'next/router'
import useThriveAuth from 'hooks/useThriveAuth'
import {
  cleanBookingInfo,
  startBooking,
  updateBookingDetails,
} from 'state/booking/actions'
import { initialBookingHelperState } from 'state/booking/constants'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import _isEmpty from 'lodash/isEmpty'
import { RudderEvent } from 'constants/rudderEvents'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import logEvent from 'utils/logEvent'
import { validateAndProceedBooking } from 'app/Bookings/utils'
import {
  queryFilters,
  getPreviousRouterUrlQuery,
  setTrackProviderInformation,
  getFilterValueFromId,
} from 'app/ProviderDetails/utils'
import { getSelectedPaymentType } from 'app/ProviderSearch/rudderEventUtils'
import { getAvailabilityFilter } from 'app/ProviderDetails/StickyScheduleSection/AppointmentToggle/utils'
import {
  RudderstackIntegrations,
  sessionStorageKeys,
  TimeOfDay,
} from 'constants/common'
import {
  AvailabilityFilterValues,
  LabelAppointmentMapping,
  SLOT_LIST_LIMIT,
  SLOT_LOCATION_MAP,
} from 'app/ProviderSearch/constants'
import styles from './index.module.scss'
import { Spinner } from 'react-bootstrap'
import PageLoader from 'common/PageLoader'
import TimeZonePicker from 'common/TimeZonePicker/V3'
import VisitTypeCheckbox from 'common/VisitTypeCheckbox'
import { SlotAvailabilityType } from 'app/Bookings/constants'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import _omit from 'lodash/omit'
import TimeOfDayCheckbox from 'common/TimeOfDayCheckbox'
import useGetUserTreatment from 'hooks/treatments/useGetUserTreatment'
import { storeBookingDetailsForV2 } from '@app/bookings/utils/bookingHelper'

type SlotGridBodyProps = {
  providerDetails: Provider
  userSelectedAvailType?: ProviderFilter[]
  showModal: boolean
  groupedFilters
  slotLocation?: string
}

const SlotGridBody = forwardRef<HTMLDivElement, SlotGridBodyProps>(
  (props, ref) => {
    const router = useRouter()
    const formattedQuery = getParsedQuery(router)
    const {
      providerDetails,
      groupedFilters,
      showModal,
      userSelectedAvailType,
      slotLocation,
    } = props
    const {
      isAuthenticated,
      loginWithRedirect,
      user,
      isSupportOrThirdPartyUser,
      isSupportUser,
      isThirdPartyAgent,
    } = useThriveAuth()
    const dispatch = useDispatch()
    const { query } = router

    const { id, practice } = providerDetails
    const { timeZone } = useContext(TimeZoneContext)

    const { dbUser, userEmail, b2bUserDetails } = useAppSelector(
      (state) => state.bookingHelper
    )

    const providerList = useMemo(() => {
      if (providerDetails) {
        return [providerDetails]
      }

      return []
    }, [providerDetails?.id])

    const startSlotDate = useMemo(() => getCurrentZonnedTime(timeZone), [
      timeZone,
    ])
    const {
      insuranceId,
      insurancePlan,
      selfPay,
      availabilityType: availabilityTypeId,
      userId,
      patients,
      counselingType,
    } = formatUserQuery(query)

    const { availabilityLabels } = getAvailabilityFilter(
      availabilityTypeId || [],
      providerDetails
    )
    const { providerSlots, loading: slotsLoading } = getProviderSlots({
      startSlotDate: startSlotDate,
      slotListLimit: SLOT_LIST_LIMIT,
      providerList: providerList,
      practiceId: practice.id,
      timeZone,
      slotsType: '',
      skipTimeOfDayFilter: true,
    })

    const initialAvailabilityMode =
      userSelectedAvailType?.length === 1
        ? userSelectedAvailType[0]?.value === AvailabilityFilterValues.inPerson
          ? SlotAvailabilityType.INPERSON
          : SlotAvailabilityType.ONLINE
        : undefined
    //undefined = show both availability / none selected
    const [
      appointmentMode,
      setAppointmentMode,
    ] = useState<SlotAvailabilityType>(initialAvailabilityMode)
    const [timeOfDay, setTimeOfDay] = useState<TimeOfDay[]>(
      formattedQuery.timeOfDay ?? []
    )
    const dateSlots: DateSlot = providerSlots[id]
    const { globalGroupedFilters } = useAppSelector((state) => state.global)
    const counselingTypeFilters =
      globalGroupedFilters[ProviderFilterTypes.COUNSELING_TYPE]

    const timeOfDayFilters = globalGroupedFilters[
      ProviderFilterTypes.TIME_OF_DAY
    ]?.length
      ? globalGroupedFilters[ProviderFilterTypes.TIME_OF_DAY]
      : groupFilters(providerDetails.filters)[ProviderFilterTypes.TIME_OF_DAY]

    const filteredDateSlotsByAppointmentMode: DateSlot = filterDateSlotsByAppointmentMode(
      dateSlots || {},
      appointmentMode
    )
    const dates: Date[] = generateDates(startSlotDate, SLOT_LIST_LIMIT)

    const generatedDatedSlots = generateDatedSlots(
      dates,
      filteredDateSlotsByAppointmentMode,
      (slot) => {
        if (
          providerDetails?.counselingTypeIds &&
          !isValidMedicationSlotWithProviderFilter(
            providerDetails,
            slot,
            counselingTypeFilters
          )
        ) {
          return null
        } else if (
          !isValidMedicationSlotWithProviderId(providerDetails, slot)
        ) {
          return null
        } else if (timeOfDay.length > 0) {
          return getSlotWithTimeOfDayAdded(slot, timeOfDay, timeZone)
        }

        return slot
      }
    )

    const providerSlotsCount = countProviderSlots(generatedDatedSlots)

    const userSelectedFilters = getFilterValueFromId(groupedFilters, query)
    const counselingTypes = groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]

    const flattenedCounselingTypeId =
      typeof formattedQuery?.counselingType === 'string'
        ? formattedQuery?.counselingType
        : formattedQuery?.counselingType?.[0]

    const counselingTypeValue = counselingTypes.find((type) => {
      return type.id === flattenedCounselingTypeId
    })

    const bookAppointment = (selectedSlot: SelectedSlot) => {
      let selectedSlotAppointmentType: SlotAvailabilityType = null

      if (selectedSlot.slot.availabilityType.length === 1) {
        selectedSlotAppointmentType =
          selectedSlot.slot.availabilityType[0] === SlotAvailabilityType.ONLINE
            ? SlotAvailabilityType.ONLINE
            : SlotAvailabilityType.INPERSON
      } else {
        selectedSlotAppointmentType = SlotAvailabilityType.ONLINE //default to video visit if none selected/ both selected
      }

      const availTypeToAddInRouterUrl = !appointmentMode
        ? selectedSlotAppointmentType
        : appointmentMode

      const mappedAvailability = Object?.keys(LabelAppointmentMapping)?.find(
        (availabilityObject) => {
          if (
            LabelAppointmentMapping[availabilityObject] ===
            availTypeToAddInRouterUrl
          ) {
            return availabilityObject
          }
        }
      )

      const matchedAvailabilityFilter = groupedFilters[
        ProviderFilterTypes.AVAILABILITY_TYPE
      ]?.find((filter) => {
        if (filter?.value === mappedAvailability) {
          return filter
        }

        return
      })
      dispatch(cleanBookingInfo(isThirdPartyAgent))
      dispatch(
        startBooking({
          ...initialBookingHelperState,
          ...(!!b2bUserDetails && { b2bUserDetails }),
          selectedSlot,
          providerDetails,
          filters: {
            slotAvailabilityTypes: selectedSlot.slot.availabilityType,
          },
          ...(!!dbUser && { dbUser, userEmail: dbUser.email }),
          ...(!!userEmail && { userEmail }), //userEmail will be available if redirecting from Support dashboard.
        })
      )
      dispatch(
        updateBookingDetails({
          providerId: providerDetails.id,
          ...(formattedQuery?.counselingType && {
            counselingTypeId: flattenedCounselingTypeId,
          }),
          counselingType: counselingTypeValue ? counselingTypeValue.value : '',
          ...(!appointmentMode
            ? { appointmentMode: selectedSlotAppointmentType }
            : { appointmentMode }),
        })
      )

      storeBookingDetailsForV2({
        selectedSlot,
        providerDetails,
        appointmentModeId: matchedAvailabilityFilter?.id,
        counselingTypeId: flattenedCounselingTypeId,
      })

      logEvent(
        RudderEvent.PROVIDER_SLOT_SELECTED,
        {
          count_provider_slots_shown: providerSlotsCount,
          slot_timestamp: selectedSlot.slot?.start,
          payment_type_filter: getSelectedPaymentType(
            getPreviousRouterUrlQuery()
          ),
          ...queryFilters(providerDetails, getPreviousRouterUrlQuery()),
          ...setTrackProviderInformation(providerDetails),
          account_id: user?.id,
          ...(slotLocation && {
            slot_location:
              showModal === true && providerSlotsCount > 0
                ? SLOT_LOCATION_MAP.availability_modal
                : slotLocation,
          }),
        },
        {
          [RudderstackIntegrations.GOOGLE_ADS]: true,
        }
      )

      const queryObject = {
        tz: timeZone,
        ...(counselingType && {
          counselingType: counselingType[0],
        }),
        ...(insuranceId && { insuranceId }),
        ...(insurancePlan && { insurancePlan }),
        ...(selfPay && { selfPay }),
        ...(userId && { userId }),
        ...(patients && { patients }),
        ...(matchedAvailabilityFilter && {
          availabilityType: matchedAvailabilityFilter.id,
        }),
        slotId: selectedSlot.slot.id,
      }

      validateAndProceedBooking(
        isAuthenticated,
        loginWithRedirect,
        router,
        queryObject
      )
    }

    useEffect(() => {
      if (showModal === true && providerSlotsCount > 0) {
        logEvent(RudderEvent.BOOKING_MODAL_ALL_AVAILABILITY_SHOWN, {
          provider_id: providerDetails.id,
          provider_name: `${providerDetails.firstName} ${providerDetails.lastName}`,
          practice_state: providerDetails.practice.state,
          filters: {
            age: userSelectedFilters?.client_age_group || 'null',
            appointment_type: userSelectedFilters?.availability_type || 'null',
            counseling_approach:
              userSelectedFilters?.counseling_approach || 'null',
            gender: userSelectedFilters?.gender || 'null',
            issues: userSelectedFilters?.issues || 'null',
            language: userSelectedFilters?.language || 'null',
            payment_type: query.insuranceId
              ? sessionStorage.getItem(
                  sessionStorageKeys.INSURANCE_PROVIDER_NAME
                )
              : 'Self Pay',
            therapy_type: userSelectedFilters?.counseling_type || 'null',
          },
          count_provider_slots_shown: providerSlotsCount,
        })
      }
    }, [showModal, providerSlotsCount])

    const isTimeOfDayFilterFeatureOn =
      useGetUserTreatment(TreatmentFlags.SHOW_TIME_OF_DAY_FILTER) ===
      treatmentFlagValues.ON

    return (
      <PageLoader
        isLoading={slotsLoading}
        Components={{
          Loading: <Spinner animation="border" />,
        }}
      >
        <div ref={ref} className={styles.ModalBody_subContainer}>
          {isSupportOrThirdPartyUser && <TimeZonePicker />}
          <VisitTypeCheckbox
            handleAppointmentMode={setAppointmentMode}
            providerName={providerDetails.firstName}
            appointmentMode={appointmentMode}
            availabilityLabels={availabilityLabels}
            userSelectedAvailType={userSelectedAvailType}
          />
          {isSupportUser && isTimeOfDayFilterFeatureOn && (
            <TimeOfDayCheckbox
              filters={timeOfDayFilters}
              timeOfDay={timeOfDay}
              setTimeOfDay={setTimeOfDay}
            />
          )}
          <SlotCard
            userSelectedAvailType={appointmentMode}
            providerDetails={providerDetails}
            dates={dates}
            showSlotSelection={false}
            generatedSlots={generatedDatedSlots}
            onSlotClick={bookAppointment}
          />
        </div>
      </PageLoader>
    )
  }
)

export default SlotGridBody
